class MobileGallery extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 0;
    this.isScrolling = false;
    this.resizeTimer = null;
    this.handleResizeBound = this.handleResize.bind(this);
    this.activeVariantImage = '';
    this.activeVariantTitle = '';
    this.activeGalleryMedia = null;
    this.inferredDesktopSecondaryClassName = '';
  }

  connectedCallback() {
    // 1. Store original content once on load
    if (!this.originalContent) {
      this.originalContent = this.innerHTML;
    }

    // 2. Run initialization
    this.init();

    // 3. Listen for resize with debounce to optimize performance
    window.addEventListener('resize', this.handleResizeBound);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.handleResizeBound);
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer);
    }
  }

  handleResize() {
    clearTimeout(this.resizeTimer);
    this.resizeTimer = setTimeout(() => {
      this.init();
    }, 150);
  }

  init() {
    const isMobile = window.innerWidth <= 767;
    const hasSlider = this.querySelector('.mobile-gallery__main') !== null;
    const loaderEnabled = this.classList.contains('mobile-loader-enabled');

    // Handle single slide mode (disable slider logic if this parent class exists)
    if (this.closest('.mobile-single-slide')) {
      if (hasSlider) this.destroy();
      this.style.display = 'block';
      return;
    }

    if (isMobile) {
      // Mobile version: Build the slider structure if it doesn't exist yet
      if (!hasSlider) {
        this.render();
        this.bindEvents();
        this.setActiveImage(0);
      }

      // If the loader feature is enabled, signal that the mobile block is initialized
      if (loaderEnabled) {
        setTimeout(() => {
          this.classList.add('mobile-loader-ready');
        }, 200);
      }

      this.style.display = 'block';
    } else {
      // Desktop version: If the slider exists, destroy it and restore original HTML
      if (hasSlider) {
        this.destroy();

        // Remove the ready class when switching back to desktop
        if (loaderEnabled) {
          this.classList.remove('mobile-loader-ready');
        }
      }
      this.style.display = 'contents';
    }

    this.applyCurrentVariantState();
  }

  render() {
    // If already rendered - exit
    if (this.querySelector('.mobile-gallery__main')) return;

    // IMPORTANT: Get elements from originalContent, not current DOM,
    // because current DOM might be empty or modified after a destroy/init cycle.
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = this.originalContent;
    const mediaItems = Array.from(tempDiv.querySelectorAll('.media-item'));

    if (mediaItems.length === 0) return;

    // Create main image container
    const mainContainer = document.createElement('div');
    mainContainer.className = 'mobile-gallery__main';
    mainContainer.setAttribute('role', 'list');

    // Create thumbnails container
    const thumbsContainer = document.createElement('div');
    thumbsContainer.className = 'mobile-gallery__thumbs';
    thumbsContainer.setAttribute('role', 'list');

    // Process media items

    const isSepareteImages = this.classList.contains('is--separate-images');

    mediaItems.forEach((item, index) => {
      let shouldRender = false;
      if (isSepareteImages && (item.classList.contains('visible-mobile') || item.classList.contains('visible-both'))) {
        shouldRender = true;
      } else if (!isSepareteImages) {
        shouldRender = true;
      }

      if (!shouldRender) return;

      // Clone for main image
      const mainItem = item.cloneNode(true);
      mainItem.className = 'mobile-gallery__main-item';
      mainItem.setAttribute('role', 'listitem');
      mainItem.dataset.index = index;
      if (index === 0) mainItem.classList.add('active');
      mainContainer.appendChild(mainItem);

      // Create thumbnail
      const thumb = document.createElement('div');
      thumb.className = 'mobile-gallery__thumb';
      thumb.setAttribute('role', 'listitem');
      thumb.dataset.index = index;
      if (index === 0) thumb.classList.add('active');

      // Handle media content for thumbnail
      const media = item.querySelector('img, video');
      if (media) {
        if (media.tagName === 'VIDEO') {
          const thumbImg = document.createElement('img');
          if (media.poster) {
            thumbImg.src = media.poster;
            thumbImg.alt = media.alt || 'Video thumbnail';
          } else {
            // Placeholder SVG
            thumbImg.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiByeD0iNCIgZmlsbD0iIzMzMzMzMyIvPgo8cGF0aCBkPSJNMTYgMTJMMjggMjBMMTYgMjhWMTJaIiBmaWxsPSJ3aGl0ZSIvPgo8L3N2Zz4K';
            thumbImg.alt = 'Video';
          }
          thumb.appendChild(thumbImg);
        } else {
          const thumbMedia = media.cloneNode(true);
          thumb.appendChild(thumbMedia);
        }
      }
      thumbsContainer.appendChild(thumb);
    });

    // Clear container and append new structure
    this.innerHTML = '';
    this.appendChild(mainContainer);
    this.appendChild(thumbsContainer);
  }

  destroy() {
    // Restore original HTML for desktop
    if (this.originalContent) {
      this.innerHTML = this.originalContent;
    }
    this.style.display = 'contents';
  }

  bindEvents() {
    // Thumbnail click events
    const thumbs = this.querySelectorAll('.mobile-gallery__thumb');
    thumbs.forEach((thumb, index) => {
      thumb.addEventListener('click', () => {
        this.setActiveImage(index);
      });
    });

    // Main image scroll events
    const mainContainer = this.querySelector('.mobile-gallery__main');
    if (mainContainer) {
      mainContainer.addEventListener('scroll', () => {
        if (this.isScrolling) return;

        const scrollLeft = mainContainer.scrollLeft;
        const itemWidth = mainContainer.clientWidth;
        const newIndex = Math.round(scrollLeft / itemWidth);

        if (newIndex !== this.currentIndex) {
          this.currentIndex = newIndex;
          this.updateActiveThumbnail();
        }
      });
    }
  }

  setActiveImage(index) {
    this.isScrolling = true;
    this.currentIndex = index;

    const mainContainer = this.querySelector('.mobile-gallery__main');
    // Guard clause in case called during destruction
    if (!mainContainer) return;

    const targetScrollLeft = index * mainContainer.clientWidth;

    mainContainer.scrollTo({
      left: targetScrollLeft,
      behavior: 'smooth'
    });

    this.updateActiveStates();

    setTimeout(() => {
      this.isScrolling = false;
    }, 300);
  }

  updateActiveThumbnail() {
    const thumbs = this.querySelectorAll('.mobile-gallery__thumb');
    thumbs.forEach((thumb, index) => {
      thumb.classList.toggle('active', index === this.currentIndex);
    });
    this.scrollThumbnailsToActive();
  }

  updateActiveStates() {
    const mainItems = this.querySelectorAll('.mobile-gallery__main-item');
    mainItems.forEach((item, index) => {
      const isActive = index === this.currentIndex;
      item.classList.toggle('active', isActive);
      item.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });
    this.updateActiveThumbnail();
  }

  scrollThumbnailsToActive() {
    const thumbsContainer = this.querySelector('.mobile-gallery__thumbs');
    const activeThumb = this.querySelector('.mobile-gallery__thumb.active');

    if (thumbsContainer && activeThumb) {
      const containerWidth = thumbsContainer.clientWidth;
      const thumbWidth = activeThumb.offsetWidth;
      const thumbLeft = activeThumb.offsetLeft;
      const scrollLeft = thumbLeft - (containerWidth / 2) + (thumbWidth / 2);

      thumbsContainer.scrollTo({
        left: Math.max(0, scrollLeft),
        behavior: 'smooth'
      });
    }
  }

  getCleanFileName(url) {
    if (!url) return '';
    return url.split('?')[0].split('/').pop().replace(/(_\d+x\d*)/, '').replace(/(_master)/, '');
  }

  normalizeGalleryItems(galleryItems) {
    if (!Array.isArray(galleryItems)) return [];

    return galleryItems
      .filter((item) => item && typeof item.src === 'string' && item.src.length)
      .map((item) => ({
        src: item.src,
        srcset: item.srcset || '',
        alt: item.alt || '',
        width: Number(item.width) || null,
        height: Number(item.height) || null
      }));
  }

  resolveGalleryMediaForViewport(galleryMediaPayload) {
    if (!galleryMediaPayload) return [];

    if (Array.isArray(galleryMediaPayload)) {
      return this.normalizeGalleryItems(galleryMediaPayload);
    }

    if (typeof galleryMediaPayload !== 'object') {
      return [];
    }

    const preferredOrder = window.innerWidth <= 767
      ? ['mobile', 'both', 'desktop']
      : ['desktop', 'both', 'mobile'];

    for (const key of preferredOrder) {
      const normalized = this.normalizeGalleryItems(galleryMediaPayload[key]);
      if (normalized.length) return normalized;
    }

    return [];
  }

  applyCurrentVariantState() {
    if (!this.activeVariantImage) return;

    const galleryItems = this.resolveGalleryMediaForViewport(this.activeGalleryMedia);
    if (window.innerWidth <= 767) {
      if (galleryItems.length) {
        this.applyMobileMetaobjectGallery(galleryItems, this.activeVariantImage, this.activeVariantTitle);
      } else {
        this.applyLegacyMobileVariantImage(this.activeVariantImage);
      }
      return;
    }

    this.applyLegacyDesktopVariantImage(this.activeVariantImage, this.activeVariantTitle);
    if (galleryItems.length) {
      this.applyDesktopMetaobjectGallery(galleryItems);
    } else {
      this.removeInjectedDesktopGalleryItems();
      this.restoreDesktopBaseGallery();
    }
  }

  applyLegacyMobileVariantImage(variantImage) {
    const targetFileName = this.getCleanFileName(variantImage);
    const images = this.querySelectorAll('.mobile-gallery__main-item img');
    let matchedIndex = -1;

    for (let i = 0; i < images.length; i += 1) {
      const currentFileName = this.getCleanFileName(images[i].src);
      if (currentFileName && (currentFileName.includes(targetFileName) || targetFileName.includes(currentFileName))) {
        const mainItem = images[i].closest('.mobile-gallery__main-item');
        if (mainItem && mainItem.dataset.index) {
          matchedIndex = Number(mainItem.dataset.index);
          break;
        }
      }
    }

    if (matchedIndex > -1) {
      this.setActiveImage(matchedIndex);
      return;
    }

    const firstMainItem = this.querySelector('.mobile-gallery__main-item[data-index="0"]');
    if (!firstMainItem) return;

    const img = firstMainItem.querySelector('img');
    const video = firstMainItem.querySelector('video, iframe');
    if (!img || video) return;

    if (!img.dataset.originalSrc) {
      img.dataset.originalSrc = img.src;
      if (img.srcset) img.dataset.originalSrcset = img.srcset;
    }
    img.src = variantImage;
    img.removeAttribute('srcset');

    const firstThumb = this.querySelector('.mobile-gallery__thumb[data-index="0"] img');
    if (firstThumb) {
      if (!firstThumb.dataset.originalSrc) {
        firstThumb.dataset.originalSrc = firstThumb.src;
      }
      firstThumb.src = variantImage;
      firstThumb.removeAttribute('srcset');
    }

    this.setActiveImage(0);
  }

  applyLegacyDesktopVariantImage(variantImage, variantTitle) {
    const originalHero = this.querySelector('.media-item:not(.variant-hero):not(.variant-metaobject-secondary)');
    if (!originalHero) return;

    let variantHero = this.querySelector('.media-item.variant-hero');
    if (!variantHero) {
      variantHero = originalHero.cloneNode(true);
      variantHero.classList.add('variant-hero');
      variantHero.dataset.mediaId = 'variant-preview';
      originalHero.parentNode.insertBefore(variantHero, originalHero);
    }

    const video = variantHero.querySelector('video, iframe, model-viewer');
    if (video) video.remove();

    let img = variantHero.querySelector('img');
    if (!img) {
      img = document.createElement('img');
      img.classList.add('product-image');
      variantHero.appendChild(img);
    }

    img.src = variantImage;
    img.removeAttribute('srcset');
    img.alt = variantTitle || 'Variant Image';
    img.style.display = 'block';

    variantHero.style.display = 'block';
    originalHero.style.display = 'none';
  }

  getDesktopSecondaryClassName() {
    if (this.inferredDesktopSecondaryClassName) {
      return this.inferredDesktopSecondaryClassName;
    }

    const originalItems = this.querySelectorAll('.media-item:not(.variant-hero):not(.variant-metaobject-secondary)');
    const referenceItem = originalItems[1] || originalItems[0];
    this.inferredDesktopSecondaryClassName = referenceItem ? referenceItem.className : 'media-item';
    return this.inferredDesktopSecondaryClassName;
  }

  applyDesktopMetaobjectGallery(galleryItems) {
    const normalized = this.normalizeGalleryItems(galleryItems);
    if (!normalized.length) return;

    const originalItems = Array.from(
      this.querySelectorAll('.media-item:not(.variant-hero):not(.variant-metaobject-secondary)')
    );
    if (!originalItems.length) return;

    this.removeInjectedDesktopGalleryItems();
    this.restoreDesktopBaseGallery();

    const heroItem = this.querySelector('.media-item.variant-hero') || originalItems[0];
    if (!heroItem || !heroItem.parentNode) return;

    originalItems.slice(1).forEach((item) => {
      item.style.display = 'none';
      item.dataset.variantGalleryHidden = 'true';
    });

    const className = this.getDesktopSecondaryClassName();
    const fragment = document.createDocumentFragment();
    normalized.forEach((mediaItem) => {
      const mediaElement = document.createElement('div');
      mediaElement.className = `${className} variant-metaobject-secondary`;
      mediaElement.dataset.variantMetaobjectSecondary = 'true';
      mediaElement.setAttribute('role', 'listitem');

      const mediaWrapper = document.createElement('div');
      mediaWrapper.className = 'media-item__media';

      const image = document.createElement('img');
      image.src = mediaItem.src;
      if (mediaItem.srcset) image.srcset = mediaItem.srcset;
      image.alt = mediaItem.alt || this.activeVariantTitle || '';
      image.loading = 'lazy';
      if (mediaItem.width) image.width = mediaItem.width;
      if (mediaItem.height) image.height = mediaItem.height;

      mediaWrapper.appendChild(image);
      mediaElement.appendChild(mediaWrapper);
      fragment.appendChild(mediaElement);
    });

    heroItem.insertAdjacentElement('afterend', fragment.firstChild);
    let insertionPoint = heroItem.nextElementSibling;
    while (fragment.firstChild) {
      insertionPoint.insertAdjacentElement('afterend', fragment.firstChild);
      insertionPoint = insertionPoint.nextElementSibling;
    }
  }

  removeInjectedDesktopGalleryItems() {
    this.querySelectorAll('[data-variant-metaobject-secondary]').forEach((node) => node.remove());
  }

  restoreDesktopBaseGallery() {
    this.querySelectorAll('[data-variant-gallery-hidden="true"]').forEach((node) => {
      node.style.display = '';
      delete node.dataset.variantGalleryHidden;
    });
  }

  applyMobileMetaobjectGallery(galleryItems, variantImage, variantTitle) {
    const mainContainer = this.querySelector('.mobile-gallery__main');
    const thumbsContainer = this.querySelector('.mobile-gallery__thumbs');
    if (!mainContainer || !thumbsContainer) return;

    const normalized = this.normalizeGalleryItems(galleryItems);
    if (!normalized.length) return;

    const firstSlideImage = this.querySelector('.mobile-gallery__main-item[data-index="0"] img');
    const heroSrc = variantImage || firstSlideImage?.src || normalized[0].src;
    const heroAlt = variantTitle || firstSlideImage?.alt || 'Variant image';

    const composedItems = [{ src: heroSrc, alt: heroAlt }, ...normalized];

    mainContainer.innerHTML = '';
    thumbsContainer.innerHTML = '';

    composedItems.forEach((item, index) => {
      const mainItem = document.createElement('div');
      mainItem.className = 'mobile-gallery__main-item';
      mainItem.setAttribute('role', 'listitem');
      mainItem.dataset.index = String(index);
      if (index === 0) mainItem.classList.add('active');

      const image = document.createElement('img');
      image.src = item.src;
      if (item.srcset) image.srcset = item.srcset;
      image.alt = item.alt || '';
      image.loading = 'lazy';
      if (item.width) image.width = item.width;
      if (item.height) image.height = item.height;
      mainItem.appendChild(image);
      mainContainer.appendChild(mainItem);

      const thumb = document.createElement('div');
      thumb.className = 'mobile-gallery__thumb';
      thumb.setAttribute('role', 'listitem');
      thumb.dataset.index = String(index);
      if (index === 0) thumb.classList.add('active');

      const thumbImage = document.createElement('img');
      thumbImage.src = item.src;
      thumbImage.alt = item.alt || '';
      thumbImage.loading = 'lazy';
      thumb.appendChild(thumbImage);
      thumbsContainer.appendChild(thumb);
    });

    this.bindEvents();
    this.setActiveImage(0);
  }

  handleVariantChange(e) {
    const variantImage = e.detail.variantImage;
    const variantTitle = e.detail.variantTitle || '';
    const galleryMediaPayload = e.detail.galleryMedia || null;

    if (!variantImage) {
      this.activeVariantImage = '';
      this.activeVariantTitle = '';
      this.activeGalleryMedia = null;
      this.resetGallery();
      return;
    }

    this.activeVariantImage = variantImage;
    this.activeVariantTitle = variantTitle;
    this.activeGalleryMedia = galleryMediaPayload;

    const galleryItems = this.resolveGalleryMediaForViewport(galleryMediaPayload);
    if (window.innerWidth <= 767) {
      if (galleryItems.length) {
        this.applyMobileMetaobjectGallery(galleryItems, variantImage, variantTitle);
      } else {
        this.applyLegacyMobileVariantImage(variantImage);
      }
      return;
    }

    this.applyLegacyDesktopVariantImage(variantImage, variantTitle);
    if (galleryItems.length) {
      this.applyDesktopMetaobjectGallery(galleryItems);
    } else {
      this.removeInjectedDesktopGalleryItems();
      this.restoreDesktopBaseGallery();
    }
  }

  resetGallery() {
    // Desktop reset
    const variantHero = this.querySelector('.media-item.variant-hero');
    const originalHero = this.querySelector('.media-item:not(.variant-hero):not(.variant-metaobject-secondary)');
    if (variantHero) variantHero.remove();
    if (originalHero) originalHero.style.display = '';

    this.removeInjectedDesktopGalleryItems();
    this.restoreDesktopBaseGallery();

    // Mobile reset
    const images = this.querySelectorAll('.mobile-gallery__main-item img');
    images.forEach((img) => {
      if (img.dataset.originalSrc) {
        img.src = img.dataset.originalSrc;
        if (img.dataset.originalSrcset) img.srcset = img.dataset.originalSrcset;
        delete img.dataset.originalSrc;
        delete img.dataset.originalSrcset;
      }
    });

    const thumbs = this.querySelectorAll('.mobile-gallery__thumb img');
    thumbs.forEach((img) => {
      if (img.dataset.originalSrc) {
        img.src = img.dataset.originalSrc;
        delete img.dataset.originalSrc;
      }
    });
  }
}

// Global initialization
document.addEventListener('DOMContentLoaded', () => {
  // Define element only if not defined (prevents errors on hot reload)
  if (!customElements.get('mobile-gallery')) {
    customElements.define('mobile-gallery', MobileGallery);
  }

  window.addEventListener('purchase-option:change', (e) => {
    const galleries = document.querySelectorAll('mobile-gallery');
    galleries.forEach(gallery => {
      if (gallery && typeof gallery.handleVariantChange === 'function') {
        gallery.handleVariantChange(e);
      }
    });
  });
});